import type { PixelFormat } from '../transcoding/types';

export interface PaintBuffer {
  width: number,
  height: number,
  pixelFormat: PixelFormat,
  pixelData: Uint8Array,
}

// Values are [0, 256>
export interface RGB {
  r: number,
  g: number,
  b: number,
}

export interface YUV {
  y: number,
  u: number,
  v: number,
}

export function rgbToYuv(rgb: RGB) {
  const r = Math.max(Math.min(rgb.r / 255.0, 1.0), 0.0);
  const g = Math.max(Math.min(rgb.g / 255.0, 1.0), 0.0);
  const b = Math.max(Math.min(rgb.b / 255.0, 1.0), 0.0);
  const y = Math.max(Math.min((0.299 * r) + (0.587 * g) + (0.114 * b), 1.0), 0.0);
  const u = Math.max(Math.min((-0.14713 * r) + (-0.28886 * g) + (0.436 * b), 0.5), -0.5);
  const v = Math.max(Math.min((0.615 * r) + (-0.51499 * g) + (-0.10001 * b), 0.5), -0.5);
  return {
    y: (y * 255.0),
    u: (u + 0.5) * 255.0,
    v: (v + 0.5) * 255.0,
  };
}

export function yuvToRgb(yuv: { y: number, u: number, v: number }) {
  const y = Math.max(Math.min(yuv.y / 255.0, 1.0), 0.0);
  const u = (Math.max(Math.min(yuv.u, 255.0), 0.0) / 255.0) - 0.5;
  const v = (Math.max(Math.min(yuv.v, 255.0), 0.0) / 255.0) - 0.5;

  let r = y + (1.13983 * v);
  let g = y - (0.39465 * u) - (0.58060 * v);
  let b = y + (2.03211 * u);

  // Clamp and scale to [0, 255]
  r = Math.round(Math.max(Math.min(r, 1.0), 0.0) * 255.0);
  g = Math.round(Math.max(Math.min(g, 1.0), 0.0) * 255.0);
  b = Math.round(Math.max(Math.min(b, 1.0), 0.0) * 255.0);

  return { r, g, b };
}

/**
 * These setPixel functions actually operates on 2x2 pixel squares.
 * The x and y coordinates are relative to half the width and height of
 * the actual video frame.
 *
 * For instance, if the frame is 1920x1080, these setPixel functions
 * will operate on a rectangle of 960x540
 */
export interface PaintContext {
  setPixelYuv: (x: number, y: number, color: YUV) => void,
  setPixelRgb: (x: number, y: number, color: RGB) => void,
}

export function createPaintContext(target: PaintBuffer): PaintContext {
  if (target.pixelFormat === 'uyvy422') {
    return new UYVY422PaintContext(target);
  } else if (target.pixelFormat === 'yuv420p') {
    return new YUV420pPaintContext(target);
  } else if (target.pixelFormat === 'rgba') {
    return new RGBAPaintContext(target);
  } else {
    throw new Error(`Unsupported pixel format: ${target.pixelFormat}`);
  }
}

export const YELLOW: YUV = rgbToYuv({ r: 255, g: 255, b: 0 });
export const BLACK: YUV = rgbToYuv({ r: 0, g: 0, b: 0 });

// End of public API

class UYVY422PaintContext implements PaintContext {
  private pitch: number;

  constructor(private target: PaintBuffer) {
    this.pitch = target.width * 2;
  }

  setPixelYuv(x: number, y: number, color: YUV) {
    if (x < 0 || x >= (this.target.width / 2) || y < 0 || y >= (this.target.height / 2)) {
      return;
    }
    const offset1 = ((y * 2) * this.pitch) + (x * 4);
    this.target.pixelData[offset1 + 0] = color.u;
    this.target.pixelData[offset1 + 1] = color.y;
    this.target.pixelData[offset1 + 2] = color.v;
    this.target.pixelData[offset1 + 3] = color.y;
    const offset2 = offset1 + this.pitch;
    this.target.pixelData[offset2 + 0] = color.u;
    this.target.pixelData[offset2 + 1] = color.y;
    this.target.pixelData[offset2 + 2] = color.v;
    this.target.pixelData[offset2 + 3] = color.y;
  }

  setPixelRgb(x: number, y: number, color: RGB) {
    const yuv = rgbToYuv(color);
    this.setPixelYuv(x, y, yuv);
  }
}

class YUV420pPaintContext implements PaintContext {
  private lumaPitch: number;
  private chromaPitch: number;
  private uPlaneOffset: number;
  private vPlaneOffset: number;

  constructor(private target: PaintBuffer) {
    this.lumaPitch = target.width;
    this.chromaPitch = Math.round(target.width * 0.5);
    const lumaPlaneSize = target.width * target.height;
    const chromaPlaneSize = Math.round(lumaPlaneSize * 0.25);
    this.uPlaneOffset = lumaPlaneSize;
    this.vPlaneOffset = lumaPlaneSize + chromaPlaneSize;
  }

  setPixelYuv(x: number, y: number, color: YUV) {
    if (x < 0 || x >= (this.target.width / 2) || y < 0 || y >= (this.target.height / 2)) {
      return;
    }
    const x2 = x * 2;
    const y2 = y * 2;
    const lumaOffset0 = (y2 * this.lumaPitch) + x2;
    const lumaOffset1 = ((y2 + 1) * this.lumaPitch) + x2;
    this.target.pixelData[lumaOffset0 + 0] = color.y;
    this.target.pixelData[lumaOffset0 + 1] = color.y;
    this.target.pixelData[lumaOffset1 + 0] = color.y;
    this.target.pixelData[lumaOffset1 + 1] = color.y;

    const chromaOffset = (y * this.chromaPitch) + x;
    this.target.pixelData[this.uPlaneOffset + chromaOffset] = color.u;
    this.target.pixelData[this.vPlaneOffset + chromaOffset] = color.v;
  }

  setPixelRgb(x: number, y: number, color: RGB) {
    const yuv = rgbToYuv(color);
    this.setPixelYuv(x, y, yuv);
  }
}

class RGBAPaintContext implements PaintContext {
  private pitch: number;

  constructor(private target: PaintBuffer) {
    this.pitch = target.width * 4;
  }

  setPixelYuv(x: number, y: number, color: YUV) {
    const rgb = yuvToRgb(color);
    this.setPixelRgb(x, y, rgb);
  }

  setPixelRgb(x: number, y: number, color: RGB) {
    if (x < 0 || x >= (this.target.width / 2) || y < 0 || y >= (this.target.height / 2)) {
      return;
    }
    const offsets = [
      (this.pitch * (y * 2)) + (x * 8),
      (this.pitch * ((y * 2) + 1)) + (x * 8),
      (this.pitch * (y * 2)) + (x * 8) + 4,
      (this.pitch * ((y * 2) + 1)) + (x * 8) + 4,
    ];
    for (const offset of offsets) {
      this.target.pixelData[offset + 0] = color.r;
      this.target.pixelData[offset + 1] = color.g;
      this.target.pixelData[offset + 2] = color.b;
      this.target.pixelData[offset + 3] = 255;
    }
  }
}
